import * as React from "react"
import checkSubVars from "../utils/checkSubVars"
import { formatDateTime } from "../utils/format"
import CsvExport from "./CsvExport"
import { useIntl } from "gatsby-plugin-intl"
import { AppState } from "../data/redux-state"
import { extractUnitFromVariableName } from "../utils/cumulParam"
import { getTranslatedGeoVariableName } from "./VariableDropdown"

var countries = require("i18n-iso-countries")
countries.registerLocale(require("i18n-iso-countries/langs/en.json"))

function isV3GeoRegion(region: GeoRegion): region is GeoRegionV3 {
  return (
    "PrimaryTS" in region &&
    typeof region.PrimaryTS === "object" &&
    region.PrimaryTS !== null
  )
}

const CSVDownloadButton = (props: AppState) => {
  const intl = useIntl()
  const csvData = [
    ["data_example_one", "data_example_two", "data_example_three"],
    ["Ahmed", "Tomi", "ah@smthing.co.com"],
    ["Raed", "Labes", "rl@smthing.co.com"],
    ["Yezzi", "Min l3b", "ymin@cocococo.com"],
  ]

  const generateCsv = () => {
    console.log("Start generation of CSV!")

    const metadata = props.sourceJson!.FileMetadata
    const mainSubvars = checkSubVars(props.sourceJson!)
    const subvariables = props.subVariable
    const georegions = props.geoRegion

    const atributesTranslator = (attr: string) => {
      switch (attr) {
        case "Data":
          return "Observed timeseries"
        case "p2Paris":
          return "Observed timeseries and path2Paris"
        case "sdevRange":
          return "Observed timeseries standard error"
        case "p05":
          return "Observed timeseries p05 bound of 90% uncertainty range"
        case "p95":
          return "Observed timeseries p95 bound of 90% uncertainty range"
        default:
          return attr
      }
    }

    let globalCsv: string[][] = []
    const shortCite =
      metadata.DataSourcesShortcite ?? metadata.DataSourcesShortCite ?? ""

    // The VarUnit as shown in the parentheses of the chart's y-axis label. The
    // .csv holds the untransformed source data, so cumul/deriv/setUnit unit
    // overrides are deliberately not applied here.
    const varUnit = extractUnitFromVariableName(
      getTranslatedGeoVariableName(
        props.indicatorClass,
        props.geoVariable!,
        intl
      )
    )

    let metadataCsv = [
      ["METADATA"],
      ["Project", metadata.ProjectName],
      ["GCCIDataProvider", metadata.ProjectDataProvider],
      ["GCCIVersion", metadata.GCCIVersion],
      ["IndicatorsClass", metadata.IndicatorsClassName],
      ["GeoDomain", metadata.GeoDomainName],
      ["VariableName", metadata.VariableName],
      ["VariableUnit", varUnit],
      ["VariableUnitName", metadata.VarUnitName],
      ["TimeResolution", metadata.TimeResolution],
      ["DataTitle", metadata.DataTitle],
      ["DataSourcesShortcite", shortCite],
      ["DataVersion", metadata.DataVersion],
    ]

    let data = [["DATA"]]
    let years = []
    for (let i = 1960; i <= 2050; i++) {
      years.push([i.toString()])
    }

    const checkLastString = (arr: string[], str: string) => {
      let lastStr = ""
      let i = arr.length - 1
      while (i >= 0 && lastStr === "") {
        if (arr[i] !== "") lastStr = arr[i]
        i--
      }
      if (lastStr === str) return true
      return false
    }

    const shouldExcludeSubSubVar = (key: string) => {
      return false
    }

    let regionsRow = ["GeoRegion"]
    let subvariablesRow = ["SubVariable"]
    let coInformationRow = ["ComponentVariables"]
    const getDataSeries = (
      regionData: GeoRegion,
      key: string
    ): GeoRegionData | undefined => {
      if (key === "Data") {
        return !isV3GeoRegion(regionData) ? regionData.Data : undefined
      }
      if (key === "PrimaryTS") {
        return isV3GeoRegion(regionData) ? regionData.PrimaryTS.Data : undefined
      }
      const coVar = (regionData as any)[key] as CoVar | undefined
      return coVar?.Data
    }

    // Number of compVar columns written per SubVariable block in DATA; counted
    // while writing them so it cannot drift from the actual column layout.
    let coVarColumnsPerSubVar: number | null = null

    for (const region of georegions) {
      for (const subvar of subvariables) {
        let regionData: GeoRegion = mainSubvars[subvar].GeoRegions[region]
        let coVarColumnsOfThisSubVar = 0
        for (const key in regionData) {
          if (shouldExcludeSubSubVar(key)) {
            continue
          }
          if (key !== "Metadata") {
            coVarColumnsOfThisSubVar++
            if (checkLastString(regionsRow, region)) regionsRow.push("")
            else regionsRow.push(region)
            if (checkLastString(subvariablesRow, subvar))
              subvariablesRow.push("")
            else subvariablesRow.push(subvar)
            // if (key === "Data") coInformationRow.push("Observed timeseries")
            coInformationRow.push(atributesTranslator(key))
            let i = 0
            for (const y of years) {
              const dataSeries = getDataSeries(regionData, key)
              const value = dataSeries ? dataSeries[y[0]] : undefined
              if (value !== undefined && value !== null) {
                years[i].push(value ? value + "" : "0.0")
              } else {
                years[i].push("")
              }
              i++
            }
          }
        }
        if (coVarColumnsPerSubVar === null) {
          coVarColumnsPerSubVar = coVarColumnsOfThisSubVar
        }
      }
    }

    data.push(regionsRow)
    data.push(subvariablesRow)
    data.push(coInformationRow)

    // Listed in the same order in which the DATA block introduces them.
    let fileInfo: string[][] = [
      [""],
      ["FILEINFO"],
      ["NoOfGeoRegions", props.geoRegion.length + ""],
      ["NoOfSubVariables", props.subVariable.length + ""],
      ["NoOfComponentVariables", (coVarColumnsPerSubVar ?? 0) + ""],
      ["NoOfYears", years.length + ""],
      [""],
    ]

    globalCsv = globalCsv.concat(metadataCsv)
    globalCsv = globalCsv.concat(fileInfo)
    globalCsv = globalCsv.concat(data)
    globalCsv = globalCsv.concat(years)
    return globalCsv
  }

  let fileName = ""
  for (let i = 0; i < props.subVariable.length; i++) {
    if (fileName === "") fileName = fileName + props.subVariable[i]
    else fileName = fileName + "_" + props.subVariable[i]
  }

  for (let i = 0; i < props.geoRegion.length; i++) {
    let countryCode = countries.getAlpha3Code(props.geoRegion[i], "en")
    if (countryCode) fileName = fileName + "_" + countryCode
    else fileName = fileName + "_" + props.geoRegion[i]
  }

  const currentDate = new Date()
  fileName = fileName + "_" + formatDateTime(currentDate)

  fileName = fileName.replace(/ /g, "_")
  fileName = `${fileName}.csv`

  return (
    // <>
    <CsvExport
      asyncExportMethod={async () => {
        return {
          data: generateCsv(),
          filename: fileName,
          headers: [],
        }
      }}
    >
      {intl.formatMessage({ id: "dropdown.downloadChartAsCsv" })}
    </CsvExport>
    // </>
  )
}

export default CSVDownloadButton
